import { motion } from 'framer-motion';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Vector SVG Flags with authentic colors and proportions
const FlagGermany = () => (
  <svg viewBox="0 0 640 480" className="w-full h-full object-cover select-none">
    <rect width="640" height="160" y="0" fill="#000000" />
    <rect width="640" height="160" y="160" fill="#DD0000" />
    <rect width="640" height="160" y="320" fill="#FFCE00" />
  </svg>
);

const FlagColombia = () => (
  <svg viewBox="0 0 640 480" className="w-full h-full object-cover select-none">
    <rect width="640" height="240" y="0" fill="#FCD116" />
    <rect width="640" height="120" y="240" fill="#003893" />
    <rect width="640" height="120" y="360" fill="#CE1126" />
  </svg>
);

const FlagUSA = () => (
  <svg viewBox="0 0 640 480" className="w-full h-full object-cover select-none">
    {/* 13 stripes */}
    <rect width="640" height="480" fill="#B22234" />
    <path
      fill="#FFFFFF"
      d="M0 36.9h640v36.9H0zm0 73.8h640v36.9H0zm0 73.9h640v36.9H0zm0 73.8h640v36.9H0zm0 73.9h640v36.9H0zm0 73.8h640v36.9H0z"
    />
    {/* Blue canton */}
    <rect width="256" height="258" fill="#3C3B6E" />
    {/* 50 stars grid */}
    <g fill="#FFFFFF">
      {[
        [22, 22], [65, 22], [108, 22], [151, 22], [194, 22], [236, 22],
        [43, 48], [86, 48], [129, 48], [172, 48], [215, 48],
        [22, 74], [65, 74], [108, 74], [151, 74], [194, 74], [236, 74],
        [43, 100], [86, 100], [129, 100], [172, 100], [215, 100],
        [22, 126], [65, 126], [108, 126], [151, 126], [194, 126], [236, 126],
        [43, 152], [86, 152], [129, 152], [172, 152], [215, 152],
        [22, 178], [65, 178], [108, 178], [151, 178], [194, 178], [236, 178],
        [43, 204], [86, 204], [129, 204], [172, 204], [215, 204],
        [22, 230], [65, 230], [108, 230], [151, 230], [194, 230], [236, 230]
      ].map(([x, y], i) => (
        <polygon
          key={i}
          points="0,-6 1.8,-1.5 6.5,-1.5 2.6,1.8 4.2,6.5 0,3.6 -4.2,6.5 -2.6,1.8 -6.5,-1.5 -1.8,-1.5"
          transform={`translate(${x}, ${y})`}
        />
      ))}
    </g>
  </svg>
);

const flagComponents = {
  germany: <FlagGermany />,
  colombia: <FlagColombia />,
  usa: <FlagUSA />
};

export const DondeEstoy = () => {
  const { t } = useLanguage();
  const data = t.dondeEstoy;

  const scrollToContact = () => {
    const el = document.getElementById('contacto');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="donde-estoy" className="py-20 lg:py-28 bg-[#0B0B0B] border-t border-[#171717]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#F5F5F5] tracking-tight mb-3"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="donde-estoy-title"
          >
            {data.title}
          </h2>
          <p className="text-xs sm:text-sm text-[#9CA3AF] tracking-[0.25em] uppercase">
            {data.subtitle}
          </p>
        </motion.div>

        {/* 5-Month Calendar Grid / Casillas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
          {data.timeline.map((item, index) => {
            const isCurrent = item.isCurrent;
            const FlagComponent = flagComponents[item.countryKey] || flagComponents.germany;

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                viewport={{ once: true }}
                className={`relative p-6 sm:p-7 rounded-2xl flex flex-col items-center justify-between text-center transition-all duration-300 group ${
                  isCurrent
                    ? 'bg-gradient-to-b from-[#18231C] via-[#121915] to-[#0E1210] border-2 border-emerald-500/50 shadow-2xl shadow-emerald-950/30 ring-1 ring-emerald-500/20'
                    : 'bg-gradient-to-b from-[#151515] via-[#111111] to-[#0D0D0D] border border-[#232323] hover:border-[#383838] hover:from-[#181818] hover:to-[#111111] shadow-xl shadow-black/40'
                }`}
                data-testid={`card-mes-${index + 1}`}
              >
                {/* Indicator / Header Pill */}
                <div className="h-7 flex items-center justify-center mb-3">
                  {isCurrent ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] tracking-wider uppercase font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 shadow-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                      <span>{data.currentStatus}</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono text-[#71717A] bg-[#1A1A1A] border border-[#27272A]">
                      <span className="text-[#A1A1AA] font-semibold">0{index + 1}</span>
                      <span className="text-[#3F3F46]">·</span>
                      <span className="text-[#888888] font-sans uppercase tracking-wider">{item.monthShort}</span>
                    </span>
                  )}
                </div>

                {/* Month Name */}
                <div className="mb-2">
                  <span className="text-xs font-mono font-medium tracking-[0.18em] text-[#D4D4D8] uppercase">
                    {item.month}
                  </span>
                </div>

                {/* Framed Natural Flag Badge */}
                <div className="my-4 relative transform group-hover:scale-105 transition-transform duration-300">
                  <div className="p-1.5 bg-[#1B1B1B] rounded-xl border border-[#2D2D2D] shadow-2xl group-hover:border-[#404040] transition-colors">
                    <div className="w-20 h-13 sm:w-24 sm:h-15 rounded-lg overflow-hidden relative shadow-inner border border-white/10 flex items-center justify-center">
                      {FlagComponent}
                      {/* Satin glass reflection overlay */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.04] to-white/[0.12] pointer-events-none" />
                    </div>
                  </div>
                </div>

                {/* City & Country with GER / COL / USA code */}
                <div className="mt-2 pt-4 border-t border-[#1F1F1F] w-full">
                  <h3
                    className="text-2xl font-light text-[#F5F5F5] tracking-tight mb-1"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {item.city}
                  </h3>
                  <div className="flex items-center justify-center gap-2 mt-1.5">
                    <span className="text-xs text-[#9CA3AF] uppercase tracking-wider font-medium">
                      {item.country}
                    </span>
                    <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-md bg-[#1C1C1C] border border-[#2D2D2D] text-[#E4E4E7]">
                      {item.countryCode}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Minimal Action CTA */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4 text-center"
        >
          <button
            onClick={scrollToContact}
            className="bg-[#E5E5E5] hover:bg-white text-[#0B0B0B] px-8 py-3 text-xs sm:text-sm font-medium tracking-wider uppercase transition-all duration-200 rounded-sm"
          >
            {data.bookButton}
          </button>

          <a
            href={`https://wa.me/573227613983?text=${encodeURIComponent('Hola Ramiro, quiero consultar disponibilidad para mi proyecto de tatuaje.')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs sm:text-sm text-[#9CA3AF] hover:text-white transition-colors px-6 py-3 tracking-wide"
          >
            <MessageCircle size={16} />
            <span>WhatsApp</span>
            <ArrowRight size={14} />
          </a>
        </motion.div>

      </div>
    </section>
  );
};
